// SmartStack: iOS Smart Stack-like widget with vertical pages and dots
// - Uses scroll snapping and dot navigation
// - Cards/tiles are clickable and navigate to /graphs
// - Hero values come from the `overview` prop forwarded by Dashboard;
//   when overview.hasOutput is false, every value renders "—"
import React, {useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './SmartStack.css';
import { BioIcon, SleepIcon, HeartRateIcon, BrainIcon, PersonIcon, LightningIcon, ArrowUpIcon, ArrowDownIcon } from './Icons';
import { BrainIcon as DashboardBrainIcon } from './DashboardIcons';
import {
  SLEEP_HEATMAP_PALETTE,
  SLEEP_HEATMAP_FALLBACK_CELLS,
  computeSleepHeatmapCells,
  hasAnySleepData,
} from './activityShared';
import { loadStoredActivities, storeActivities, subscribeToActivityUpdates } from '../utils/activityStore';
import { fetchActivitiesFromBackend } from '../services/activityService';

// Sleep Heatmap — last 21 days of sleep from logged activities. Most recent
// day is bottom-right; tone gets darker the longer that day's total sleep.
// When the user has no sleep activities yet we render a deterministic demo
// grid so the dashboard never looks empty (matches the Graphs page behaviour).
const SleepHeatmap = ({ activities }) => {
  const grid = useMemo(() => {
    return hasAnySleepData(activities)
      ? computeSleepHeatmapCells(activities)
      : SLEEP_HEATMAP_FALLBACK_CELLS;
  }, [activities]);
  return (
    <div className="sleep-heatmap">
      {grid.map((row, rowIdx) => (
        <div key={rowIdx} className="heatmap-row">
          {row.map((paletteIdx, colIdx) => (
            <div
              key={colIdx}
              className="heatmap-cell"
              style={{ backgroundColor: SLEEP_HEATMAP_PALETTE[paletteIdx] }}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

// Heart Rate Mini Chart for Avg HR tile
const HRMiniChart = () => {
 return (
 <div className="hr-mini-chart">
 <svg width="60" height="30" viewBox="0 0 60 30">
 {[15, 22, 18, 25, 20, 28, 15, 20, 18, 22].map((h, i) => (
 <rect 
 key={i} 
 x={i * 6} 
 y={30 - h} 
 width="4" 
 height={h} 
 rx="2" 
 fill="#FF6B6B"
 />
 ))}
 </svg>
 <span className="hr-mini-label">~99</span>
 </div>
 );
};

// Reusable Metric Tile matching Figma style
const MetricTile = ({ label, value, subLabel, icon, color, chart, onClick }) => (
 <div className={`ss-tile figma-tile ${onClick ? 'clickable' : ''}`} onClick={onClick}>
 <div className="ss-tile-header figma-header">
 {icon && <div className="ss-tile-icon figma-icon" style={{ color: color }}>{icon}</div>}
 <span className="ss-tile-label figma-label">{label}</span>
 </div>
 {chart && <div className="tile-chart">{chart}</div>}
 <div className="ss-tile-value-row figma-value-row">
 <span className="ss-tile-value figma-value">{value}</span>
 {subLabel && <span className="ss-tile-sub figma-sub">{subLabel}</span>}
 </div>
 </div>
);

/** Pulls a numeric field off the overview snapshot when hasOutput is
 *  true and the value is finite. Returns null otherwise. The caller
 *  formats null into "—" for display so empty cards never show a
 *  hardcoded placeholder that could be mistaken for real data. */
function pickOverviewValue(overview, key) {
  if (!overview || !overview.hasOutput) return null;
  const v = overview[key];
  return Number.isFinite(v) ? v : null;
}

/** Format a numeric backend value for tile display. Null → "—". */
function fmt(value, suffix = '') {
  if (value == null) return '—';
  // /10 scores typically have 1 decimal; integers stay clean.
  const text = value % 1 !== 0 ? value.toFixed(1) : String(Math.round(value));
  return suffix ? `${text}${suffix}` : text;
}

// Heatmap karelerini boyarken kullandığımız aynı 5'li bin yapısı.
// Sleep kartının altındaki "Quality" yüzdesi de aynı eşiklere göre
// hesaplanıyor — böylece görsel ton ile rakam birbiriyle çelişmiyor.
// 0 dk → null (UI "—" gösterir), 7+ saat → 95.
function minutesToQualityPercent(min) {
  if (!min || min <= 0) return null;
  if (min < 180) return 30;
  if (min < 300) return 55;
  if (min < 420) return 80;
  return 95;
}

// Aktivite listesinden o gün kaydedilmiş toplam uyku süresini (dakika)
// çıkartır. Aynı tarihte birden fazla "sleep" girişi varsa toplanır.
function getTodaySleepMinutes(activities) {
  if (!Array.isArray(activities) || activities.length === 0) return 0;
  const d = new Date();
  const todayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  let total = 0;
  for (const a of activities) {
    if (!a || a.category !== 'sleep') continue;
    if (a.date !== todayKey) continue;
    const mins = Number(a.durationMin) || 0;
    if (mins > 0) total += mins;
  }
  return total;
}

export default function SmartStack({
 // No more prop defaults — these are kept as escape hatches for tests
 // / standalone usage but production data flows in via `overview`.
 biologicalAge: propBiologicalAge,
 // Kullanıcının kayıt formundan girdiği gerçek yaşı. Dashboard.js
 // burayı profile API'sinden besliyor. BioGraph'in better/worse
 // tonunu belirlemek için biological age ile kıyaslanır; null'sa
 // default "better" tonunda kalır.
 actualAge: propActualAge,
 avgHeartRate: propAvgHeartRate,
 // Activities can be passed in by the parent (Dashboard does this for the
 // sleep heatmap) — but if they're omitted we fall back to loading from
 // localStorage ourselves so this widget works in isolation too.
 activities: activitiesProp,
 // The overview snapshot from /models/output/overview. Dashboard
 // forwards this directly. When present and hasOutput=true, every
 // hero number is pulled from here. When absent or hasOutput=false,
 // tiles render "—" instead of a hardcoded mock.
 overview,
}) {
 const containerRef = useRef(null);
 const [active, setActive] = useState(0);
 const slides = [0, 1];
 const navigate = useNavigate();

 // ─── Activities for the sleep heatmap ───
 // Mirrors RecentActivitiesWidget's pattern: localStorage on mount, then
 // best-effort backend sync, plus subscribe to in-app updates and a
 // gentle 5-second poll. Skipped entirely when the parent passes
 // activities in directly (avoids two sources of truth).
 const [internalActivities, setInternalActivities] = useState(() =>
   activitiesProp ? null : loadStoredActivities()
 );

 useEffect(() => {
   if (activitiesProp) return; // parent owns the data; nothing to do here.

   const loadFromStorage = () => setInternalActivities(loadStoredActivities());
   loadFromStorage();

   // Backend sync — pull the FULL list (not just the latest few) so the
   // 21-day heatmap has every sleep entry it needs to draw older days.
   // CRITICAL: also persist to localStorage right away. The 5-second
   // interval below will re-read from localStorage; without this write
   // the heatmap state gets overwritten with whatever stale/partial set
   // localStorage happened to have, and older days flicker away.
   (async () => {
     const full = await fetchActivitiesFromBackend();
     if (full && full.length) {
       setInternalActivities(full);
       storeActivities(full); // ← keeps localStorage in sync with backend
     }
   })();

   const unsubscribe = subscribeToActivityUpdates(loadFromStorage);
   const handleStorage = (e) => {
     if (e.key === 'hrv4.activities') loadFromStorage();
   };
   window.addEventListener('storage', handleStorage);
   const interval = setInterval(loadFromStorage, 5000);

   return () => {
     unsubscribe();
     window.removeEventListener('storage', handleStorage);
     clearInterval(interval);
   };
 }, [activitiesProp]);

 const activities = activitiesProp ?? internalActivities ?? [];

 // Resolve hero values from overview when present, otherwise from
 // explicit props (rare — used by tests / isolated rendering),
 // otherwise null. JSX uses fmt() to turn null into "—".
 const biologicalAge = pickOverviewValue(overview, 'biologicalAge') ?? propBiologicalAge ?? null;
 const actualAge = propActualAge ?? null;

 // Sleep — bugün için aktiviteden gelen toplam dakika. Quality, heatmap
 // boyamasında kullandığımız aynı bin yapısıyla yüzdeye çevrilir.
 // Aktivite yoksa süre/quality null kalır → JSX "—" gösterir.
 const todaySleepMin = getTodaySleepMinutes(activities);
 const sleepHours = todaySleepMin > 0 ? Math.floor(todaySleepMin / 60) : null;
 const sleepMins = todaySleepMin > 0 ? todaySleepMin % 60 : null;
 const sleepDurationText =
   sleepHours != null && sleepMins != null
     ? `${sleepHours}h ${sleepMins}m`
     : '—';
 const sleepQuality = minutesToQualityPercent(todaySleepMin);

 const burnoutResistance10 = pickOverviewValue(overview, 'burnoutResistance');
 const performance10 = pickOverviewValue(overview, 'performance');
 const stressProcessing10 = pickOverviewValue(overview, 'processingOfStress');
 const healthState10 = pickOverviewValue(overview, 'healthState');

 // Performance is shown as a percent in the bottom row. Backend
 // sends a 0-10 score so we scale; null stays null and renders "—".
 const performancePotentialDisplay =
   performance10 != null ? `${Math.round(performance10 * 10)}%` : '—';

 const overviewAvgHr = pickOverviewValue(overview, 'avgHr');
 const overviewMaxHr = pickOverviewValue(overview, 'maxHr');
 const overviewMinHr = pickOverviewValue(overview, 'minHr');
 const avgHeartRate = (overviewAvgHr && overviewAvgHr > 0)
   ? overviewAvgHr
   : (propAvgHeartRate ?? null);
 const maxHeartRate = (overviewMaxHr && overviewMaxHr > 0) ? overviewMaxHr : null;
 const minHeartRate = (overviewMinHr && overviewMinHr > 0) ? overviewMinHr : null;
 
 // BioGraph yuvarlağının doldurma yüzdesi backend'den doğrudan
 // geliyor (`biologicalAgePercentile`) — Graphs sayfasının kullandığı
 // `biologicalAgePercentage` ile aynı kaynaktan üretilen bir skor.
 const ageDiffPercent = pickOverviewValue(overview, 'biologicalAgePercentile');

 // Yön bilgisi (better / worse) backend'den gelmiyor — percentile
 // hep pozitif. Bunun için kullanıcının gerçek yaşı ile biyolojik
 // yaşını kıyaslıyoruz: biologicalAge > actualAge ise vücut yaşından
 // daha yaşlı görünüyor → "higher". İkisinden biri yoksa default
 // "better" tonu — gauge ilk açılışta kırmızıya kaymasın.
 const isAgeBetter =
   biologicalAge != null && actualAge != null
     ? biologicalAge <= actualAge
     : true;

 const onScroll = () => {
 const el = containerRef.current;
 if (!el) return;
 const idx = Math.round(el.scrollTop / el.clientHeight);
 if (idx !== active) setActive(idx);
 };
 const scrollToIndex = (idx) => {
 const container = containerRef.current;
 if (!container) return;
 const pages = container.querySelectorAll('.ss-page');
 const pageTop = pages[idx]?.offsetTop ?? idx * container.clientHeight;
 container.scrollTo({ top: pageTop, behavior: 'smooth' });
 };

 const goToGraph = (section, metric) => {
 navigate(`/graphs?section=${section}&metric=${metric}`);
 };

 // --- Gauge Component ---
 const Gauge = ({ score, isBetter = true }) => {
 const size = 110;
 const radius = size / 2;
 const strokeWidth = 8;
 const r = radius - strokeWidth / 2;
 const circumference = 2 * Math.PI * r;

 // No backend value yet — draw the empty ring only and skip the
 // colour fill / comparison text. Keeps the card visually anchored.
 const isEmpty = score == null || !Number.isFinite(score);
 const safeScore = isEmpty ? 0 : score;
 const strokeDashoffset = circumference - (safeScore / 100) * circumference;

 // Colors: green for better, red for worse
 const strokeColor = isBetter ? '#AFF4EE' : '#ef4444';
 const comparisonText = isBetter ? 'better' : 'higher';

 return (
 <div className="ss-gauge-wrapper">
 <svg width={size} height={size}>
 <circle cx={radius} cy={radius} r={r} stroke="#E1E1E1" strokeWidth={strokeWidth} fill="none" />
 {!isEmpty && (
 <circle cx={radius} cy={radius} r={r} stroke={strokeColor} strokeWidth={strokeWidth} fill="none" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" transform={`rotate(-90 ${radius} ${radius})`} />
 )}
 </svg>
 <div className="ss-gauge-content">
 <div className="ss-gauge-stack">
 <div className="ss-gauge-value">{isEmpty ? '—' : `%${safeScore}`}</div>
 {!isEmpty && (
 <>
 <div className="ss-gauge-note">{comparisonText}</div>
 <div className="ss-gauge-note">than your age</div>
 </>
 )}
 </div>
 </div>
 </div>
 );
 };

 return (
 <div className="ss-widget">
 <div className="ss-scroll" ref={containerRef} onScroll={onScroll}>
 {/* --- Slide 1: Bio Age + Sleep on top, 4 tiles below --- */}
 <section className="ss-page figma-page">
 <div className="figma-grid">
 {/* Top Row: Bio Age (left) + Sleep (right) */}
 <div className="top-row">
 {/* Biological Age Widget */}
 <div className="ss-card ss-age-card figma-age clickable" onClick={() => goToGraph('trend', 'biological-age')}>
 <div className="ss-header-row figma-header-row">
 <span className="ss-title figma-title">Biological Age</span>
 <div className="ss-icon-bg figma-icon-bg"><BioIcon size={17} /></div>
 </div>
 <div className="ss-age-content figma-age-content">
 <div className="age-text-above">
 <span className="ss-big-number figma-age-value">{biologicalAge ?? '—'}</span>
 <span className="age-unit">years</span>
 </div>
 <div className="ss-gauge-center">
 <Gauge score={ageDiffPercent} isBetter={isAgeBetter} />
 </div>
 </div>
 </div>

 {/* Sleep Widget */}
 <div className="ss-card ss-sleep-card figma-sleep clickable" onClick={() => goToGraph('overview', 'sleep-quality')}>
 <div className="ss-header-row figma-header-row">
 <span className="ss-title figma-title">Sleep</span>
 <div className="ss-icon-bg figma-icon-bg sleep-icon-bg"><SleepIcon size={17} /></div>
 </div>
 <div className="sleep-content">
 <SleepHeatmap activities={activities} />
 <div className="sleep-stats">
 <div className="sleep-duration">
 <span className="duration-value">{sleepDurationText}</span>
 </div>
<div className="sleep-quality good">
  <span className="quality-percent">
    {sleepQuality != null ? `${sleepQuality}%` : '—'}
  </span>
  <span className="quality-label">Quality</span>
</div>
 </div>
 </div>
 </div>
 </div>

 {/* Bottom Row: 4 Metric Tiles */}
 <div className="bottom-tiles">
 {/* Custom HR Tile — empty until we wire up the HR summary endpoint */}

 <MetricTile
 label="Burnout Res."
 value={fmt(burnoutResistance10)}
 subLabel={burnoutResistance10 != null ? '/10' : ''}
 icon={<BrainIcon size={14} />}
 color="#8B5CF6"
 onClick={() => goToGraph('overview', 'burnout-resilience')}
 />
 <MetricTile
 label="Performance Potential"
 value={performancePotentialDisplay}
 icon={<PersonIcon size={14} />}
 color="#10B981"
 onClick={() => goToGraph('overview', 'performance')}
 />
 <MetricTile
 label="Health Score"
 value={fmt(healthState10)}
 subLabel={healthState10 != null ? '/10' : ''}
 icon={<LightningIcon size={14} />}
 color="#F59E0B"
 onClick={() => goToGraph('distribution', 'general-health')}
 />
 </div>
 </div>
 </section>

 {/* --- Slide 2: Additional Metrics --- */}
 <section className="ss-page">
 <div className="ss-grid-tiles figma-slide">
 <MetricTile label="Processing of Stress" value={fmt(stressProcessing10)} subLabel={stressProcessing10 != null ? '/10' : ''} icon={<DashboardBrainIcon size={14} color="#10B981" />} color="#10B981" onClick={() => goToGraph('trend', 'stress')} />
 <MetricTile label="Avg HR" value={avgHeartRate ?? '—'} subLabel={avgHeartRate != null ? 'bpm' : ''} icon={<HeartRateIcon size={14} />} color="#FF2D55" onClick={() => goToGraph('trend', 'performance')} />
 <MetricTile label="Max HR" value={maxHeartRate ?? '—'} subLabel={maxHeartRate != null ? 'bpm' : ''} icon={<ArrowUpIcon size={14} />} color="#FF2D55" onClick={() => goToGraph('trend', 'performance')} />
 <MetricTile label="Min HR" value={minHeartRate ?? '—'} subLabel={minHeartRate != null ? 'bpm' : ''} icon={<ArrowDownIcon size={14} />} color="#30B0C7" onClick={() => goToGraph('distribution', 'general-health')} />
 </div>
 </section>

 </div>

 {/* Widget Dots (Navigation) */}
 <div className="ss-dots">
 {slides.map((i) => (
 <button key={i} className={`ss-dot ${active === i ? 'active' : ''}`} onClick={() => scrollToIndex(i)} />
 ))}
 </div>
 </div>
 );
}